import type { Person } from "../session-types.ts";
type Publication = { isMuted: boolean } | null | undefined;

const HEX_DIRECTIONS = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

function axialRing(radius: number) {
  if (!radius) return [[0, 0]];
  const positions = [];
  let q = -radius;
  let r = radius;
  for (const [dq, dr] of HEX_DIRECTIONS) {
    for (let step = 0; step < radius; step += 1) {
      positions.push([q, r]);
      q += dq;
      r += dr;
    }
  }
  return positions;
}

function visualPosition([q, r]: number[]) {
  return { x: q + r / 2, y: r * 0.866 };
}

export function participantCloudLayout(count: number) {
  if (count <= 0) return [];
  const positions = [];
  for (let radius = 0; positions.length < count; radius += 1) {
    const ring = axialRing(radius).map(visualPosition);
    const offset = radius % 2 ? Math.floor(ring.length / 2) : 0;
    for (let index = 0; index < ring.length; index += 1) {
      positions.push(ring[(index + offset) % ring.length]);
      if (positions.length === count) break;
    }
  }
  const minX = Math.min(...positions.map(({ x }) => x));
  const maxX = Math.max(...positions.map(({ x }) => x));
  const minY = Math.min(...positions.map(({ y }) => y));
  const maxY = Math.max(...positions.map(({ y }) => y));
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  return positions.map(({ x, y }) => ({
    x: Number((x - centerX).toFixed(3)),
    y: Number((y - centerY).toFixed(3)),
  }));
}

export function participantMotion(id: unknown) {
  let hash = 2166136261;
  for (const character of String(id)) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  const unsigned = hash >>> 0;
  return {
    delay: -((unsigned % 2400) / 1000),
    duration: 3.8 + ((unsigned >>> 8) % 1400) / 1000,
    tilt: ((unsigned >>> 16) % 5) - 2,
    driftX: (unsigned & 1 ? 1 : -1) * (1 + ((unsigned >>> 12) % 3)),
    driftY: (unsigned & 2 ? 1 : -1) * (1 + ((unsigned >>> 20) % 3)),
    returnX: (unsigned & 4 ? 1 : -1) * (1 + ((unsigned >>> 4) % 2)),
    returnY: (unsigned & 8 ? 1 : -1) * (1 + ((unsigned >>> 24) % 2)),
  };
}

export function participantCanOpenScreen(
  participant: Pick<Person, "screen"> | null | undefined,
  watchedKinds: string[] = [],
) {
  return Boolean(participant?.screen || watchedKinds.includes("screen"));
}

export function participantHasMicrophoneFeed(
  participant: Pick<Person, "self"> | null | undefined,
  localPublication: Publication,
  remotePublication: Publication,
) {
  const publication = participant?.self ? localPublication : remotePublication;
  return Boolean(publication && !publication.isMuted);
}

export function participantVoiceStatus(
  participant: Pick<Person, "self" | "voicesMuted"> | null | undefined,
  localPublication: Publication,
  remotePublication: Publication,
) {
  return {
    microphoneOff: !participantHasMicrophoneFeed(participant, localPublication, remotePublication),
    notListening: Boolean(participant?.voicesMuted),
  };
}

function orbitPoint(radius: number, angle: number, distance = radius) {
  return { x: radius + distance * Math.cos(angle), y: radius + distance * Math.sin(angle) };
}

function orbitArc(radius: number, start: number, end: number) {
  const from = orbitPoint(radius, start);
  const to = orbitPoint(radius, end);
  return `M ${from.x} ${from.y} A ${radius} ${radius} 0 0 ${end > start ? 1 : 0} ${to.x} ${to.y}`;
}

export function participantBadgeGeometry(
  size: number,
  {
    hasScreen = false,
    liveTextWidth = 0,
    microphoneOff = false,
    notListening = false,
    connecting = false,
    liveProgress = hasScreen ? 1 : 0,
    voiceProgress = microphoneOff || notListening || connecting ? 1 : 0,
    voiceSeparation = microphoneOff && notListening ? 28 : 0,
  }: {
    hasScreen?: boolean;
    liveTextWidth?: number;
    microphoneOff?: boolean;
    notListening?: boolean;
    connecting?: boolean;
    liveProgress?: number;
    voiceProgress?: number;
    voiceSeparation?: number;
  } = {},
) {
  const radius = size / 2;
  const voiceKinds = connecting
    ? []
    : [microphoneOff && "microphone", notListening && "headphones"].filter(Boolean);
  const thickness = 24;
  const endPadding = 8;
  const separation = Math.max(0, voiceSeparation);
  const labelWidth = Math.max(1, liveTextWidth);
  const livePathLength = hasScreen ? Math.max(1, labelWidth + 2 * endPadding - thickness) : 0;
  // The invisible text guide reaches into both rounded caps, leaving 8px of visible end padding.
  const textPathExtension = 10;
  const liveFootprint = hasScreen ? livePathLength + thickness : 0;
  const voiceEndExtension = endPadding + 15 / 2 - thickness / 2;
  const voicePathLength = connecting
    ? 18 + 2 * endPadding - thickness
    : voiceKinds.length
      ? separation + 2 * voiceEndExtension
      : 0;
  const voiceFootprint = connecting || voiceKinds.length ? voicePathLength + thickness : 0;
  const liveAngle = -Math.PI / 2;
  const voiceAngle = Math.PI / 2;
  const live = hasScreen
    ? (() => {
        const centerAngle = liveAngle;
        const length = livePathLength;
        const start = centerAngle - length / (2 * radius);
        const end = centerAngle + length / (2 * radius);
        return {
          path: orbitArc(radius, start, end),
          textPath: orbitArc(
            radius,
            start - textPathExtension / radius,
            end + textPathExtension / radius,
          ),
          textOffset: textPathExtension + length / 2,
          pathLength: length,
          textPathLength: length + 2 * textPathExtension,
          anchor: orbitPoint(radius, centerAngle),
          angle: centerAngle,
          footprint: liveFootprint,
          thickness,
        };
      })()
    : null;

  const voice =
    connecting || voiceKinds.length
      ? (() => {
          const centerAngle = voiceAngle;
          const start = centerAngle - voicePathLength / (2 * radius);
          const end = centerAngle + voicePathLength / (2 * radius);
          return {
            path: orbitArc(radius, start, end),
            icons:
              voiceKinds.length === 2
                ? [
                    orbitPoint(radius, centerAngle - separation / (2 * radius)),
                    orbitPoint(radius, centerAngle + separation / (2 * radius)),
                  ]
                : [orbitPoint(radius, centerAngle)],
            divider:
              voiceKinds.length === 2
                ? [
                    orbitPoint(radius, centerAngle, radius - 9),
                    orbitPoint(radius, centerAngle, radius + 9),
                  ]
                : null,
            anchor: orbitPoint(radius, centerAngle),
            angle: centerAngle,
            pathLength: voicePathLength,
            footprint: voiceFootprint,
            thickness,
          };
        })()
      : null;

  const cuts = [
    live &&
      liveProgress > 0 &&
      `<g transform="translate(${live.anchor.x} ${live.anchor.y}) scale(${liveProgress}) translate(${-live.anchor.x} ${-live.anchor.y})"><path d="${live.path}" fill="none" stroke="black" stroke-width="${live.thickness + 6}" stroke-linecap="round"/></g>`,
    voice &&
      voiceProgress > 0 &&
      `<g transform="translate(${voice.anchor.x} ${voice.anchor.y}) scale(${voiceProgress}) translate(${-voice.anchor.x} ${-voice.anchor.y})"><path d="${voice.path}" fill="none" stroke="black" stroke-width="${voice.thickness + 6}" stroke-linecap="round"/></g>`,
  ]
    .filter(Boolean)
    .join("");
  const mask = cuts
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><defs><mask id="cut"><rect width="${size}" height="${size}" fill="white"/>${cuts}</mask></defs><rect width="${size}" height="${size}" fill="white" mask="url(#cut)"/></svg>`
    : "";
  return {
    live,
    voice,
    voiceKinds,
    maskImage: mask ? `url("data:image/svg+xml,${encodeURIComponent(mask)}")` : "",
  };
}

export function participantRowOffsets(
  participants: Person[],
  avatarSize = 128,
  cameraSize = 190,
  gap = 14,
) {
  const active = participants.filter((person) => person.screen || person.camera);
  const inactive = participants.filter((person) => !person.screen && !person.camera);
  const middle = active.length ? active : inactive;
  const sides = active.length ? inactive : [];
  const size = (person: Person) => (person.camera ? cameraSize : avatarSize);
  const offsets = new Map();
  const middleWidth =
    middle.reduce((width, person) => width + size(person), 0) +
    Math.max(0, middle.length - 1) * gap;
  let cursor = -middleWidth / 2;
  for (const person of middle) {
    offsets.set(person.id, cursor + size(person) / 2);
    cursor += size(person) + gap;
  }
  let left = -middleWidth / 2 - gap;
  for (const person of sides.slice(0, Math.ceil(sides.length / 2)).reverse()) {
    offsets.set(person.id, left - size(person) / 2);
    left -= size(person) + gap;
  }
  let right = middleWidth / 2 + gap;
  for (const person of sides.slice(Math.ceil(sides.length / 2))) {
    offsets.set(person.id, right + size(person) / 2);
    right += size(person) + gap;
  }
  return offsets;
}
